import {AtUri} from '@atproto/syntax'

import {isInvalidHandle} from '#/lib/strings/handles'
import {canonicalProfilePath} from '#/lib/strings/profile-path'
import {type app} from '#/lexicons'

/**
 * Emits the canonical `/:name` form ("PDS hostname move and public URL
 * scheme") whenever `info.handle` is a real Sunnahsky/Catcher handle;
 * falls back to the `/profile/{handleOrDid}` alias for everything else -
 * an outside handle, an invalid one, or when only a DID is meaningful (no
 * canonical /:name form exists for a bare DID). `segments` is shared
 * between both forms unchanged: every segment vocabulary this function is
 * ever called with (followers, follows, known-followers, post/:rkey and
 * its liked-by/reposted-by/quotes children) has an identically-shaped
 * canonical sibling route.
 */
export function makeProfileLink(
  info: {
    did: string
    handle: string
  },
  ...segments: string[]
) {
  if (info.handle && !isInvalidHandle(info.handle)) {
    try {
      return [canonicalProfilePath(info.handle), ...segments].join('/')
    } catch {
      // Not a Sunnahsky/Catcher handle - fall through to the alias below.
    }
  }
  let handleSegment = info.did
  if (info.handle && !isInvalidHandle(info.handle)) {
    handleSegment = info.handle
  }
  return [`/profile`, handleSegment, ...segments].join('/')
}

export function makeListLink(did: string, rkey: string, ...segments: string[]) {
  return [`/profile`, did, 'lists', rkey, ...segments].join('/')
}

export function makeTagLink(did: string) {
  return `/search?q=${encodeURIComponent(did)}`
}

export function makeSearchLink(props: {query: string; from?: 'me' | string}) {
  return `/search?q=${encodeURIComponent(
    props.query + (props.from ? ` from:${props.from}` : ''),
  )}`
}

export function makeStarterPackLink(
  starterPackOrName:
    | app.bsky.graph.defs.StarterPackViewBasic
    | app.bsky.graph.defs.StarterPackView
    | string,
  rkey?: string,
) {
  if (typeof starterPackOrName === 'string') {
    return `https://bsky.app/start/${starterPackOrName}/${rkey}`
  } else {
    const uriRkey = new AtUri(starterPackOrName.uri).rkey
    return `https://bsky.app/start/${starterPackOrName.creator.handle}/${uriRkey}`
  }
}
